import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageShell from '../components/chrome/PageShell';
import { PeopleYouMayKnow } from '../components';
import friendService from '../services/friendService';
import useMediaQuery from '../utils/useMediaQuery';
import { CloseFriendMark, MirrorRoom, useCloseFriend } from '../components/closefriends';
import { A, AVATAR_TINTS, Avatar, EmptyCard, PageTitle, Pill, RADIUS, fillFor } from '../components/account/kit';

/**
 * /friends (Sept 2026 redesign, handoff §3): FRIENDS + the counts line, a search pill that
 * filters the list as you type, FRIENDS · RECEIVED · SENT tabs, and one row card per person.
 * The close-friends mark sits in the row's action area and the Mirror Room plays inside the
 * card (`surface="friendsRow"`). REMOVE is behind "···" → REMOVE FRIEND / KEEP.
 *
 * Only the top section changed: the People you may know cards below are untouched.
 */

function getInstagramHandle(socialLink) {
  if (!socialLink) return null;
  const match = socialLink.match(/instagram\.com\/([^/?]+)/);
  if (match) return match[1];
  return socialLink.replace(/^@/, '');
}

const QUOTE_FONT = "'Times New Roman', Times, serif";
const sinceLabel = (iso) => (iso ? `FRIENDS SINCE ${new Date(iso).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }).toUpperCase()}` : '');
const hit = (q, p) => !q || (p.displayName || '').toLowerCase().includes(q) || (p.handle || '').toLowerCase().includes(q);

export default function FriendRequestsPage() {
  const navigate = useNavigate();
  const phone = useMediaQuery('(max-width: 639px)');

  const [tab, setTab] = useState('friends');
  const [q, setQ] = useState('');
  const [friends, setFriends] = useState([]);
  const [received, setReceived] = useState([]);
  const [sent, setSent] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      const [friendsResult, pendingResult, sentResult] = await Promise.all([
        friendService.getFriends(),
        friendService.getPendingRequests(),
        friendService.getSentRequests(),
      ]);
      if (cancelled) return;
      if (friendsResult.success) setFriends(friendsResult.data || []);
      else setError(friendsResult.error || 'Failed to load friends');
      if (pendingResult.success) setReceived(pendingResult.data || []);
      if (sentResult.success) setSent(sentResult.data || []);
      setIsLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const openProfile = (p) => navigate(p?.handle ? `/${p.handle}` : `/users/${p?.userId}`);

  const handleRespond = async (requestId, accept) => {
    setBusyId(requestId);
    const result = await friendService.respondToRequest(requestId, accept);
    if (result.success) {
      setReceived((prev) => prev.filter((r) => r.id !== requestId));
      if (accept) {
        const friendsResult = await friendService.getFriends();
        if (friendsResult.success) setFriends(friendsResult.data || []);
      }
    }
    setBusyId(null);
  };

  const handleCancel = async (requestId) => {
    setBusyId(requestId);
    const result = await friendService.cancelRequest(requestId);
    if (result.success) setSent((prev) => prev.filter((r) => r.id !== requestId));
    setBusyId(null);
  };

  const handleRemoveFriend = async (friendUserId) => {
    setBusyId(friendUserId);
    const result = await friendService.removeFriend(friendUserId);
    if (result.success) setFriends((prev) => prev.filter((f) => f.userId !== friendUserId));
    setBusyId(null);
    setConfirmId(null);
  };

  const query = q.trim().toLowerCase();
  const friendRows = useMemo(
    () => [...friends].sort((a, b) => (a.displayName || '').localeCompare(b.displayName || '', undefined, { sensitivity: 'base' })).filter((p) => hit(query, p)),
    [friends, query],
  );
  const receivedRows = useMemo(() => received.filter((r) => hit(query, r.senderProfile || {})), [received, query]);
  const sentRows = useMemo(
    () => [...sent].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).filter((r) => hit(query, r.receiverProfile || {})),
    [sent, query],
  );

  const rows = tab === 'friends' ? friendRows : tab === 'received' ? receivedRows : sentRows;
  const empty = {
    friends: query ? ['NO MATCHES.', `Nobody on your list matches “${q.trim()}”.`] : ['NO FRIENDS YET.', 'Go to an event. Add who you meet.'],
    received: query ? ['NO MATCHES.', `Nobody who asked matches “${q.trim()}”.`] : ['0 RECEIVED.', 'Requests from people you’ve met show up here.'],
    sent: query ? ['NO MATCHES.', `Nobody you’ve asked matches “${q.trim()}”.`] : ['NOTHING PENDING.', 'Requests you send wait here until they answer.'],
  }[tab];
  const sortLabel = tab === 'friends' ? 'A–Z' : tab === 'sent' ? 'NEWEST FIRST' : '';

  const tabButton = (key, label, count) => {
    const on = tab === key;
    return (
      <button
        key={key}
        type="button"
        onClick={() => { setTab(key); setConfirmId(null); }}
        aria-pressed={on}
        className={on ? 'pr-acct-pill' : 'pr-acct-pill pr-acct-pill-white'}
        style={{ display: 'flex', alignItems: 'center', gap: 8, height: 40, padding: '0 18px', border: '2px solid transparent', borderRadius: RADIUS.pill, background: on ? fillFor('on') : A.white, color: on ? '#fff' : A.ink, fontFamily: 'inherit', fontSize: 11, fontWeight: 700, letterSpacing: '.14em', cursor: 'pointer', whiteSpace: 'nowrap', boxSizing: 'border-box', transition: 'background-color .2s, color .2s' }}
      >
        {label}
        <span style={{ fontSize: 10, fontWeight: 800, color: on ? A.live : A.grey500 }}>{count}</span>
      </button>
    );
  };

  return (
    <PageShell bare>
      <main style={{ maxWidth: 960, margin: '0 auto', padding: phone ? '20px 16px 64px' : '32px 28px 80px', boxSizing: 'border-box', color: A.ink, display: 'flex', flexDirection: 'column', gap: 24 }}>
        <PageTitle
          phone={phone}
          title="FRIENDS"
          meta={<><span style={{ color: A.ink, fontWeight: 700 }}>{friends.length} FRIENDS</span><span>·</span><span>{received.length} RECEIVED</span><span>·</span><span>{sent.length} SENT</span></>}
        />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, height: 56, padding: '0 8px 0 20px', borderRadius: RADIUS.pill, background: A.white, boxSizing: 'border-box' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2.5" strokeLinecap="square" style={{ flexShrink: 0 }} aria-hidden><circle cx="11" cy="11" r="7" /><path d="M21 21l-5-5" /></svg>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name or handle…"
              aria-label="Search your friends"
              className="pr-acct-input"
              style={{ flex: 1, minWidth: 0, height: 40, border: 0, background: 'transparent', fontFamily: 'inherit', fontSize: 14, outline: 'none', padding: 0 }}
            />
            {q && (
              <button type="button" onClick={() => setQ('')} aria-label="Clear search" className="pr-acct-invert" style={{ width: 40, height: 40, border: 0, borderRadius: '50%', background: A.off, fontFamily: 'inherit', fontSize: 14, fontWeight: 700, cursor: 'pointer', transition: 'background-color .2s, color .2s' }}>×</button>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {tabButton('friends', 'FRIENDS', friends.length)}
              {tabButton('received', 'RECEIVED', received.length)}
              {tabButton('sent', 'SENT', sent.length)}
            </div>
            {sortLabel && <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.14em', color: A.grey500 }}>{sortLabel}</span>}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {isLoading ? (
            <div style={{ padding: '40px 0', textAlign: 'center', fontSize: 11, fontWeight: 700, letterSpacing: '.14em', color: A.grey500 }}>LOADING…</div>
          ) : error ? (
            <EmptyCard title="COULDN’T LOAD YOUR FRIENDS." body={error} />
          ) : rows.length === 0 ? (
            <EmptyCard title={empty[0]} body={empty[1]} />
          ) : tab === 'friends' ? (
            friendRows.map((friend, i) => (
              <FriendRow
                key={friend.userId}
                friend={friend}
                index={i}
                phone={phone}
                onOpen={() => openProfile(friend)}
                confirming={confirmId === friend.userId}
                onAskRemove={() => setConfirmId(friend.userId)}
                onKeep={() => setConfirmId(null)}
                onRemove={() => handleRemoveFriend(friend.userId)}
                busy={busyId === friend.userId}
              />
            ))
          ) : (
            rows.map((request, i) => {
              const person = tab === 'received' ? request.senderProfile : request.receiverProfile;
              const busy = busyId === request.id;
              return (
                <PersonRow
                  key={request.id}
                  person={person || {}}
                  index={i + 3}
                  phone={phone}
                  onOpen={() => openProfile(person)}
                  bio={request.message || ''}
                  meta={tab === 'received' ? 'REQUEST RECEIVED' : 'REQUEST SENT'}
                  ig={getInstagramHandle(person?.socialLink)}
                  actions={tab === 'received' ? (
                    <>
                      <Pill kind="primary" onClick={() => handleRespond(request.id, true)} disabled={busy}>ACCEPT</Pill>
                      <Pill kind="secondary" onClick={() => handleRespond(request.id, false)} disabled={busy}>DENY</Pill>
                    </>
                  ) : (
                    <Pill kind="secondary" onClick={() => handleCancel(request.id)} disabled={busy}>CANCEL</Pill>
                  )}
                />
              );
            })
          )}
        </div>

        {/* Below the list: unchanged. */}
        <div style={{ marginTop: 8 }}>
          <PeopleYouMayKnow limit={6} />
        </div>
      </main>
    </PageShell>
  );
}

/** One row card: avatar · name @handle · “bio” · meta, actions on the right (a full-width row on phones). */
export function PersonRow({ person, index = 0, phone, onOpen, bio, meta, ig, tag, actions, children, style }) {
  const tint = AVATAR_TINTS[index % AVATAR_TINTS.length];
  return (
    <article
      className="pr-acct-row-hover"
      style={{
        position: 'relative', overflow: 'hidden', display: 'grid',
        gridTemplateColumns: phone ? '48px minmax(0,1fr)' : '56px minmax(0,1fr) auto',
        gap: phone ? '10px 12px' : 16, alignItems: 'center',
        padding: phone ? 12 : '14px 16px 14px 14px', borderRadius: RADIUS.row, background: A.white,
        transition: 'background-color .2s', ...style,
      }}
    >
      <button type="button" onClick={onOpen} aria-label={`View ${person.displayName || 'profile'}`} style={{ display: 'contents', cursor: 'pointer' }}>
        <Avatar src={person.avatarUrl} name={person.displayName} tint={tint} size={phone ? 48 : 56} />
      </button>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', minWidth: 0 }}>
          <button type="button" onClick={onOpen} style={{ padding: 0, border: 0, background: 'none', fontFamily: 'inherit', fontSize: 14, fontWeight: 700, color: A.ink, cursor: 'pointer', textAlign: 'left' }}>
            {person.displayName}
          </button>
          {person.handle && <span style={{ fontSize: 11, color: A.grey500 }}>@{person.handle}</span>}
          {tag}
        </div>
        {bio && (
          <span style={{ fontFamily: QUOTE_FONT, fontStyle: 'italic', fontSize: phone ? 15 : 17, lineHeight: 1.3, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
            “{bio}”
          </span>
        )}
        {(meta || ig) && (
          <span style={{ display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: 10, letterSpacing: '.1em', color: A.grey400 }}>
            {meta && <span>{meta}</span>}
            {ig && <span>IG @{ig}</span>}
          </span>
        )}
      </div>
      <div style={{ position: 'relative', zIndex: 2, display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end', gridColumn: phone ? '1 / -1' : 'auto' }}>
        {actions}
      </div>
      {children}
    </article>
  );
}

/** A FRIENDS-tab row: the close-friends mark + "···", or REMOVE FRIEND / KEEP once asked. */
function FriendRow({ friend, index, phone, onOpen, confirming, onAskRemove, onKeep, onRemove, busy }) {
  const close = useCloseFriend(friend.userId, friend.isClose, friend.displayName);
  const tag = close.flash ? (
    <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.12em', padding: '3px 8px', borderRadius: RADIUS.pill, background: A.ink, color: '#fff', whiteSpace: 'nowrap' }}>
      CLOSE FRIEND · THEY'LL KNOW
    </span>
  ) : null;
  const meta = close.error ? <span role="alert" style={{ color: A.ink, fontWeight: 700 }}>{close.error}</span> : sinceLabel(friend.friendsSince);
  return (
    <PersonRow
      person={friend}
      index={index}
      phone={phone}
      onOpen={onOpen}
      bio={friend.vibeCheck || ''}
      meta={meta}
      tag={tag}
      actions={confirming ? (
        <>
          <Pill kind="danger" onClick={onRemove} disabled={busy}>{busy ? '…' : 'REMOVE FRIEND'}</Pill>
          <Pill kind="secondary" onClick={onKeep} disabled={busy}>KEEP</Pill>
        </>
      ) : (
        <>
          <CloseFriendMark size={40} on={close.on} q={close.q} onToggle={close.toggle} name={friend.displayName} className="pr-acct-mark" />
          <button
            type="button"
            onClick={onAskRemove}
            aria-label={`More options for ${friend.displayName}`}
            className="pr-acct-invert"
            style={{ width: 40, height: 40, border: 0, borderRadius: '50%', background: A.off, color: A.ink, fontFamily: 'inherit', fontSize: 14, fontWeight: 700, letterSpacing: '.1em', cursor: 'pointer', transition: 'background-color .2s, color .2s', flexShrink: 0 }}
          >
            ···
          </button>
        </>
      )}
    >
      <MirrorRoom q={close.q} surface="friendsRow" first={close.first} />
    </PersonRow>
  );
}
